import { useDatetimeFormatter } from '../../../common/formatting/datetime';
import { ZonedDateTime } from '@internationalized/date';
import { useCallback, useRef, useState } from 'react';
import { isBoolean } from 'lodash';
import { DateValue } from 'react-aria-components';
import { RangeValue } from '@react-types/shared';
import { DatePickerChildrenProps } from './date-picker.interfaces';
import { toTimeAgnosticMoment } from './date-picker.utils';

export const useRenderDate = (
  withTime = false,
): ((date: ZonedDateTime) => string) => {
  const { dateFormat, dateTimeFormat } = useDatetimeFormatter();

  const format = withTime ? dateTimeFormat : dateFormat;

  return (date: ZonedDateTime) => {
    return toTimeAgnosticMoment(date.toLocaleString()).format(format);
  };
};

export const useDatepickerProps = ({
  isOpen,
  isDisabled = false,
  onChange: _onChange,
}) => {
  const [_isOpen, _setIsOpen] = useState(!!isOpen);
  const [_isWrapperLoaded, _setIsWrapperLoaded] = useState(false);
  const onWrapperMount = useCallback(() => _setIsWrapperLoaded(true), []);

  const stylesRef = useRef(null);

  const isExternallyControlled = isDisabled || isBoolean(isOpen);

  const toggleInternalOpen = useCallback(
    () => !isExternallyControlled && _setIsOpen(!_isOpen),
    [_isOpen, isExternallyControlled],
  );

  const closeInternalOpen = useCallback(
    () => !isExternallyControlled && _setIsOpen(false),
    [isExternallyControlled],
  );

  const shouldCloseOnInteractOutside = useCallback(() => {
    closeInternalOpen();
    return true;
  }, [closeInternalOpen]);

  const onChange = useCallback(
    (dateOrRange: DateValue | RangeValue<DateValue>) => {
      _onChange(dateOrRange);

      // close on click
      closeInternalOpen();
    },
    [_onChange, closeInternalOpen],
  );

  const wrapperProps = {
    ref: stylesRef,
    onMount: onWrapperMount,
  };
  const datePickerChildrenProps: Omit<
    DatePickerChildrenProps,
    'formattedDate' | 'CalendarComponent'
  > = {
    toggleInternalOpen,
    isExternallyControlled,
    isPopoverOpen: (!isExternallyControlled && _isOpen) || isOpen,
    portalContainer: _isWrapperLoaded ? stylesRef?.current : undefined,
    shouldCloseOnInteractOutside,
  };

  return {
    onChange,
    wrapperProps,
    datePickerChildrenProps,
  };
};
